import React, {useRef, useState} from "react";
import {Card, CardBody, CardHeader, Col, Container, Modal, ModalBody, ModalFooter, ModalHeader, Row} from "reactstrap";
import {Breadcrumbs, Btn, H5} from "../../../../AbstractElements";
import ButtonIcon from "../../../../CommonElements/ButtonIcon";
import ContactForm from "../Component/ContactForm";
import ContactTable from "../Component/ContactTable";
import type {Contact, ContactRelationParams, ContactTableRef} from "../Types/ContactTypes";
import SearchOptions from "../../../CommonComponents/Search";

const ContactIndex: React.FC<ContactRelationParams> = ({related_key, related_id, type, id}) => {
    const [params, setParams] = useState<Record<string, unknown>>({});
    const [searchVisibility, setSearchVisibility] = useState<boolean>(false);
    const [contactModalVisibility, setContactModalVisibility] = useState<boolean>(false);
    const [selectedContact, setSelectedContact] = useState<Contact | undefined>();

    const contactTableRef = useRef<ContactTableRef>(null);

    const searchOptions = [
        {name: 'contact', label: 'Contact'},
        {name: 'related_key', label: 'Related Key'},
        {name: 'related_id', label: 'Related ID'},
    ];

    const scopedRelatedKey = related_key || type;
    const scopedRelatedId = typeof related_id !== 'undefined' ? related_id : id;
    const canCreateContact = Boolean(scopedRelatedKey) && typeof scopedRelatedId !== 'undefined';

    const contactModalToggle = () => {
        if (contactModalVisibility) {
            setSelectedContact(undefined);
        }
        setContactModalVisibility(!contactModalVisibility);
    };

    const addContact = (contact: Contact) => {
        contactTableRef.current?.addContact(contact);
        contactModalToggle();
    };

    const getContacts = () => {
        contactTableRef.current?.getContacts();
    };

    const openEditModal = (contact: Contact) => {
        setSelectedContact(contact);
        setContactModalVisibility(true);
    };

    return <div>
        <Breadcrumbs mainTitle="Contacts" parent="System Core" title="Contacts"/>
        <Container fluid={true}>
            <Row>
                <Col sm="12">
                    <Card>
                        <CardHeader className='d-flex align-items-center justify-content-between'>
                            <H5>Contacts</H5>
                            <div className='d-flex align-items-center gap-2'>
                                {canCreateContact && <ButtonIcon
                                    attrBtn={{onClick: () => setContactModalVisibility(true)}}
                                    type={'add'}
                                    permission={'create-contact'}
                                />}
                                <ButtonIcon
                                    attrBtn={{onClick: getContacts}}
                                    type={'sync'}
                                    permission={'read-contact'}
                                />
                                <ButtonIcon
                                    attrBtn={{onClick: () => setSearchVisibility(!searchVisibility)}}
                                    type={'search'}
                                    permission={'read-contact'}
                                />
                            </div>
                        </CardHeader>
                        <CardBody>
                            {searchVisibility && (
                                <SearchOptions
                                    callBack={(searchParams: Record<string, unknown>) => setParams(searchParams)}
                                    searchOptions={searchOptions}
                                />
                            )}
                            <ContactTable ref={contactTableRef}
                                          params={params}
                                          onEdit={openEditModal}
                                          related_key={scopedRelatedKey}
                                          related_id={scopedRelatedId}/>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
        <Modal isOpen={contactModalVisibility && (canCreateContact || Boolean(selectedContact))}
               toggle={contactModalToggle}
               centered>
            <ModalHeader>{selectedContact ? 'Update' : 'Create'} Contact</ModalHeader>
            <ModalBody>
                <ContactForm callBack={addContact}
                             contact={selectedContact}
                             related_key={scopedRelatedKey}
                             related_id={scopedRelatedId}/>
            </ModalBody>
            <ModalFooter>
                <Btn attrBtn={{color: 'primary', onClick: contactModalToggle}}>Close</Btn>
            </ModalFooter>
        </Modal>
    </div>;
};

export default ContactIndex;

